import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  FormGroup,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import {
  getDrivers,
  getDriverAccess,
  updateDriverAccess,
  getDistricts,
  getDriverActiveDeliveries,
  reassignDeliveries,
  getTransferHistory,
} from "../../api/deliveryCompanyService";
import { DeliveryStatusColors } from "../Admin/constants";

const COUNTRY_PAIRS = [
  { key: "IL-IL", originCountry: "IL", destinationCountry: "IL", label: "IL → IL" },
  { key: "IL-PS", originCountry: "IL", destinationCountry: "PS", label: "IL → PS" },
  { key: "PS-IL", originCountry: "PS", destinationCountry: "IL", label: "PS → IL" },
  { key: "PS-PS", originCountry: "PS", destinationCountry: "PS", label: "PS → PS" },
];

export default function DriverAccess() {
  const { t } = useTranslation(["dcDriverAccess", "dcShared"]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);

  function loadList() {
    setLoading(true);
    getDrivers()
      .then((data) => {
        setRows(data);
        setError("");
      })
      .catch(() => setError("dcDriverAccess:loadError"))
      .finally(() => setLoading(false));
  }

  useEffect(loadList, []);

  async function openDetail(userId) {
    const data = await getDriverAccess(userId);
    setDetail(data);
  }

  const columns = useMemo(
    () => [
      { field: "fullName", headerName: t("dcShared:name"), flex: 1, minWidth: 180 },
      { field: "phone", headerName: t("dcShared:phone"), width: 140, valueGetter: (v) => v || "-" },
      {
        field: "isAvailable",
        headerName: t("dcShared:available"),
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? t("dcShared:available") : t("dcShared:offline")}
            color={params.value ? "success" : "default"}
          />
        ),
      },
      {
        field: "hasFullAccess",
        headerName: t("dcDriverAccess:access"),
        width: 160,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? t("dcDriverAccess:fullAccess") : t("dcDriverAccess:restricted")}
            color={params.value ? "success" : "warning"}
          />
        ),
      },
    ],
    [t]
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t("dcDriverAccess:title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(error)}
        </Alert>
      )}

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.userId}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          onRowClick={(params) => openDetail(params.row.userId)}
          sx={{ border: "none" }}
        />
      </Box>

      <MoveOrdersSection drivers={rows} />

      <Drawer anchor="right" open={Boolean(detail)} onClose={() => setDetail(null)}>
        {detail && (
          <Box sx={{ width: 420, p: 3 }}>
            <Typography variant="h6">{detail.fullName}</Typography>

            <CompanyScopeSummary detail={detail} />

            <AccessForm
              detail={detail}
              onSaved={(updated) => {
                setDetail(updated);
                loadList();
              }}
            />
          </Box>
        )}
      </Drawer>
    </Box>
  );
}

function CompanyScopeSummary({ detail }) {
  const { t } = useTranslation("dcDriverAccess");
  return (
    <Box sx={{ my: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {t("companyAccess")}
      </Typography>
      {detail.companyHasFullAccess ? (
        <Typography variant="body2">{t("companyFullAccess")}</Typography>
      ) : (
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          {detail.companyAllowedPairs.length > 0 && (
            <Typography variant="body2">
              {t("pairs", {
                pairs: detail.companyAllowedPairs.map((p) => `${p.originCountry}→${p.destinationCountry}`).join(", "),
              })}
            </Typography>
          )}
          {detail.companyAllowedDistricts.length > 0 && (
            <Typography variant="body2">{t("districts", { districts: detail.companyAllowedDistricts.join(", ") })}</Typography>
          )}
          {detail.companyAllowedPairs.length === 0 && detail.companyAllowedDistricts.length === 0 && (
            <Typography variant="body2">{t("noAreas")}</Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

function AccessForm({ detail, onSaved }) {
  const { t } = useTranslation("dcDriverAccess");
  const [mode, setMode] = useState(detail.hasFullAccess ? "full" : "restricted");
  const [selected, setSelected] = useState(
    new Set(detail.allowedPairs.map((p) => `${p.originCountry}-${p.destinationCountry}`))
  );
  const [districts, setDistricts] = useState(detail.allowedDistricts ?? []);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const availablePairs = detail.companyHasFullAccess
    ? COUNTRY_PAIRS
    : COUNTRY_PAIRS.filter((p) =>
        detail.companyAllowedPairs.some(
          (cp) => cp.originCountry === p.originCountry && cp.destinationCountry === p.destinationCountry
        )
      );

  useEffect(() => {
    if (detail.companyHasFullAccess) {
      getDistricts()
        .then(setDistrictOptions)
        .catch(() => setDistrictOptions([]))
        .finally(() => setDistrictsLoading(false));
    } else {
      setDistrictOptions(detail.companyAllowedDistricts ?? []);
      setDistrictsLoading(false);
    }
  }, [detail.companyHasFullAccess, detail.companyAllowedDistricts]);

  function togglePair(key) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  async function handleSave() {
    setError("");
    if (mode === "restricted" && selected.size === 0 && districts.length === 0) {
      setError("selectAtLeastOne");
      return;
    }
    setSaving(true);
    try {
      const pairs = COUNTRY_PAIRS.filter((p) => selected.has(p.key)).map(
        ({ originCountry, destinationCountry }) => ({ originCountry, destinationCountry })
      );
      const updated = await updateDriverAccess(
        detail.userId,
        mode === "full",
        pairs,
        mode === "restricted" ? districts : []
      );
      onSaved(updated);
    } catch {
      setError("saveError");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{t(error)}</Alert>}

      <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
        <FormControlLabel value="full" control={<Radio />} label={t("modeFull")} />
        <FormControlLabel value="restricted" control={<Radio />} label={t("modeRestricted")} />
      </RadioGroup>

      {mode === "restricted" && (
        <>
          <Typography variant="subtitle2">{t("countryPairs")}</Typography>
          <FormGroup sx={{ pl: 2 }}>
            {availablePairs.map((p) => (
              <FormControlLabel
                key={p.key}
                control={<Checkbox checked={selected.has(p.key)} onChange={() => togglePair(p.key)} />}
                label={p.label}
              />
            ))}
            {availablePairs.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t("noCountryPairs")}
              </Typography>
            )}
          </FormGroup>

          <Typography variant="subtitle2">{t("districtsTitle")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
            {t("districtsHelp")}
          </Typography>
          <Autocomplete
            multiple
            options={districtOptions}
            loading={districtsLoading}
            value={districts}
            onChange={(e, value) => setDistricts(value)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} placeholder={districts.length ? "" : t("selectDistricts")} size="small" />
            )}
          />
        </>
      )}

      <Button variant="contained" disabled={saving} onClick={handleSave}>
        {t("saveAccess")}
      </Button>
    </Stack>
  );
}

function groupByCity(deliveries, sortOrder, unknownCity) {
  const groups = new Map();
  for (const delivery of deliveries) {
    const city = delivery.dropoffCity || unknownCity;
    if (!groups.has(city)) groups.set(city, []);
    groups.get(city).push(delivery);
  }
  const dir = sortOrder === "newest" ? -1 : 1;
  const byOrderDate = (a, b) => dir * (new Date(a.orderCreatedAt) - new Date(b.orderCreatedAt));

  return [...groups.entries()]
    .map(([city, items]) => ({ city, items: [...items].sort(byOrderDate) }))
    .sort((a, b) => byOrderDate(a.items[0], b.items[0]));
}

// Oldest orders are the most urgent to deliver first (first-come-first-served), so this flags
// how long an order has been waiting rather than just showing a raw timestamp.
function describeWait(t, orderCreatedAt) {
  const hours = (Date.now() - new Date(orderCreatedAt).getTime()) / 36e5;
  const label = hours < 1 ? t("waitLessThanHour") : t("waitHours", { hours: Math.floor(hours) });
  const color = hours >= 6 ? "error" : hours >= 2 ? "warning" : "default";
  return { label, color };
}

// "" = all movable statuses; 1-3 are the not-yet-delivered DeliveryStatus values.
const MOVABLE_STATUS_FILTERS = ["", 1, 2, 3];

function MoveOrdersSection({ drivers }) {
  const { t } = useTranslation(["dcDriverAccess", "dcShared", "boShared"]);
  const [fromDriverId, setFromDriverId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("oldest");
  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState("");
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const fromDriver = drivers.find((d) => d.userId === fromDriverId) || null;

  function loadDeliveries(driverId, status) {
    setSelected(new Set());
    if (!driverId) {
      setDeliveries([]);
      return;
    }
    setLoadingDeliveries(true);
    getDriverActiveDeliveries(driverId, status || undefined)
      .then((data) => setDeliveries(data.items || []))
      .catch(() => setDeliveries([]))
      .finally(() => setLoadingDeliveries(false));
  }

  useEffect(() => {
    loadDeliveries(fromDriverId, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDriverId, statusFilter]);

  const unknownCity = t("dcDriverAccess:unknownCity");
  const groups = useMemo(
    () => groupByCity(deliveries, sortOrder, unknownCity),
    [deliveries, sortOrder, unknownCity]
  );

  function toggleOne(deliveryId) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(deliveryId)) next.delete(deliveryId);
      else next.add(deliveryId);
      return next;
    });
  }

  function toggleGroup(items) {
    const ids = items.map((i) => i.deliveryId);
    const allSelected = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }

  function handleMoved(count, driverName) {
    setSnackbar(
      t("dcDriverAccess:movedSnackbar", { orders: t("boShared:orderCount", { count }), name: driverName })
    );
    setMoveDialogOpen(false);
    setHistoryRefreshKey((k) => k + 1);
    loadDeliveries(fromDriverId, statusFilter);
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t("dcDriverAccess:moveTitle")}
      </Typography>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
        <Box sx={{ flex: 2, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
            <Autocomplete
              options={drivers}
              getOptionLabel={(d) => d.fullName}
              isOptionEqualToValue={(a, b) => a.userId === b.userId}
              value={fromDriver}
              onChange={(e, value) => setFromDriverId(value ? value.userId : null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("dcShared:driver")}
                  placeholder={t("dcDriverAccess:selectDriverPlaceholder")}
                  size="small"
                />
              )}
              sx={{ width: 260 }}
            />

            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              size="small"
              onChange={(e, value) => {
                if (value !== null) setStatusFilter(value);
              }}
            >
              {MOVABLE_STATUS_FILTERS.map((value) => (
                <ToggleButton key={value || "all"} value={value}>
                  {value ? t(`dcShared:deliveryStatus.${value}`) : t("dcDriverAccess:all")}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Select size="small" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} sx={{ minWidth: 190 }}>
              <MenuItem value="oldest">{t("dcDriverAccess:oldestFirst")}</MenuItem>
              <MenuItem value="newest">{t("dcDriverAccess:newestFirst")}</MenuItem>
            </Select>
          </Box>

          {!fromDriverId && (
            <Alert severity="info">{t("dcDriverAccess:selectDriverInfo")}</Alert>
          )}

          {fromDriverId && !loadingDeliveries && deliveries.length === 0 && (
            <Alert severity="info">
              {statusFilter
                ? t("dcDriverAccess:noStatusOrders", { status: t(`dcShared:deliveryStatus.${statusFilter}`) })
                : t("dcDriverAccess:noActiveOrders")}
            </Alert>
          )}

          {fromDriverId && groups.length > 0 && (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("dcDriverAccess:selectedCount", {
                    selected: selected.size,
                    orders: t("boShared:orderCount", { count: deliveries.length }),
                  })}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  disabled={selected.size === 0}
                  onClick={() => setMoveDialogOpen(true)}
                >
                  {t("dcDriverAccess:moveToDriver")}
                </Button>
              </Stack>

              {groups.map(({ city, items }, groupIndex) => {
                const ids = items.map((i) => i.deliveryId);
                const allSelected = ids.every((id) => selected.has(id));
                const someSelected = !allSelected && ids.some((id) => selected.has(id));
                return (
                  <Accordion key={city} defaultExpanded={groupIndex === 0 || groups.length <= 3}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleGroup(items)}
                        />
                        <Typography sx={{ flex: 1 }}>{city}</Typography>
                        <Chip size="small" label={t("boShared:orderCount", { count: items.length })} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <List dense disablePadding>
                        {items.map((delivery, index) => {
                          const wait = describeWait(t, delivery.orderCreatedAt);
                          return (
                            <ListItemButton
                              key={delivery.deliveryId}
                              onClick={() => toggleOne(delivery.deliveryId)}
                              sx={{ borderRadius: 1 }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox
                                  edge="start"
                                  checked={selected.has(delivery.deliveryId)}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              {sortOrder === "oldest" && groupIndex === 0 && index === 0 && (
                                <Chip size="small" label={t("dcDriverAccess:nextUp")} color="error" variant="outlined" sx={{ mr: 1 }} />
                              )}
                              <ListItemText
                                primary={`#${delivery.orderNumber} — ${delivery.customerName}`}
                                secondary={`${delivery.dropoffStreet || ""}${
                                  delivery.dropoffStreet ? ", " : ""
                                }${delivery.dropoffCity || ""}`}
                              />
                              <Chip size="small" label={wait.label} color={wait.color} sx={{ ml: 1 }} />
                              <Chip
                                size="small"
                                label={t(`dcShared:deliveryStatus.${delivery.status}`, { defaultValue: String(delivery.status) })}
                                color={DeliveryStatusColors[delivery.status] || "default"}
                                sx={{ ml: 1 }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 280, width: { xs: "100%", md: "auto" } }}>
          <TransferHistory refreshKey={historyRefreshKey} />
        </Box>
      </Stack>

      {moveDialogOpen && (
        <MoveOrdersDialog
          open={moveDialogOpen}
          onClose={() => setMoveDialogOpen(false)}
          drivers={drivers.filter((d) => d.userId !== fromDriverId)}
          deliveryIds={[...selected]}
          onMoved={handleMoved}
        />
      )}

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar("")}
        message={snackbar}
      />
    </Box>
  );
}

function MoveOrdersDialog({ open, onClose, drivers, deliveryIds, onMoved }) {
  const { t } = useTranslation(["dcDriverAccess", "dcShared", "boShared"]);
  const [toDriver, setToDriver] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleConfirm() {
    if (!toDriver) {
      setError("dcDriverAccess:chooseTargetDriver");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await reassignDeliveries(deliveryIds, toDriver.userId, reason || undefined);
      onMoved(deliveryIds.length, toDriver.fullName);
    } catch {
      setError("dcDriverAccess:moveError");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {t("dcDriverAccess:moveDialogTitle", { orders: t("boShared:orderCount", { count: deliveryIds.length }) })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{t(error)}</Alert>}
          <Autocomplete
            options={drivers}
            getOptionLabel={(d) => d.fullName}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            value={toDriver}
            onChange={(e, value) => setToDriver(value)}
            renderInput={(params) => (
              <TextField {...params} label={t("dcDriverAccess:moveToDriver")} size="small" autoFocus />
            )}
          />
          <TextField
            label={t("dcDriverAccess:reasonLabel")}
            placeholder={t("dcDriverAccess:reasonPlaceholder")}
            size="small"
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("dcShared:cancel")}
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={saving}>
          {t("dcDriverAccess:move")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TransferHistory({ refreshKey }) {
  const { t } = useTranslation(["dcDriverAccess", "dcShared"]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getTransferHistory({ page: 1, pageSize: 10 })
      .then((data) => setLogs(data.items || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  return (
    <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t("dcDriverAccess:recentTransfers")}
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {loading && (
        <Typography variant="body2" color="text.secondary">
          {t("dcShared:loading")}
        </Typography>
      )}
      {!loading && logs.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t("dcDriverAccess:noTransfers")}
        </Typography>
      )}
      <Stack spacing={1.5}>
        {logs.map((log) => (
          <Box key={log.deliveryReassignmentId}>
            <Typography variant="body2">
              {t("dcDriverAccess:transferLine", {
                number: log.orderNumber,
                from: log.fromDriverName || t("dcShared:unassigned"),
                to: log.toDriverName,
              })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(log.createdAt).toLocaleString()}
              {log.reason ? ` · ${log.reason}` : ""}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
